import styled from 'styled-components';
import { media } from './StyledCalendarBase';

// Contenedor superior de la vista Día.
// Tiene el selector de días y el botón + propio de esta vista.
export const DaySelector = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    padding: 10px 24px;
    flex-shrink: 0;
    border-bottom: 1px solid hsl(220, 13%, 91%);
    
    ${media.tablet} {
        gap: 12px;
        padding: 8px 18px;
    }
    
    ${media.mobile} {
        gap: 8px;
        padding: 8px 12px;
        align-items: center;
    }
`;

// Grupo scrolleable horizontal con los días de la semana.
// Lo separamos del botón + para que el + quede siempre visible.
export const DaySelectorDays = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 18px;
    flex: 1;
    min-width: 0;
    
    ${media.tablet} {
        gap: 12px;
    }
    
    ${media.mobile} {
        gap: 8px;
        overflow-x: auto;
        justify-content: flex-start;
        
        &::-webkit-scrollbar {
            height: 4px;
        }
        
        &::-webkit-scrollbar-track {
            background: hsl(220, 13%, 95%);
            border-radius: 2px;
        }
        
        &::-webkit-scrollbar-thumb {
            background: hsl(220, 13%, 70%);
            border-radius: 2px;
        }
    }
`;

// Botón de cada día dentro del selector semanal.
export const DaySelectorItem = styled.button`
    border: none;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 5px;
    cursor: pointer;
    padding: 0;
    
    ${media.tablet} {
        gap: 4px;
    }
    
    ${media.mobile} {
        flex-shrink: 0;
        min-width: 48px;
    }
`;

// Texto del día: Lunes, Martes, etc.
export const DaySelectorLabel = styled.span`
    font-size: 12px;
    font-weight: 600;
    color: hsl(0, 0%, 35%);
    
    ${media.tablet} {
        font-size: 11px;
    }
    
    ${media.mobile} {
        font-size: 11px;
    }
`;

// Círculo con el número del día.
export const DaySelectorCircle = styled.div`
    width: 34px;
    height: 34px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 13px;
    color: ${props => props.active ? '#FFFFFF' : 'hsl(0, 0%, 30%)'};
    background: ${props => props.active ? 'hsl(24, 85%, 60%)' : 'transparent'};
    border: ${props => props.active ? 'none' : '1px solid hsl(220, 13%, 80%)'};
    transition: background 0.2s ease, border 0.2s ease;

    &:hover {
        border-color: hsl(24, 85%, 60%);
    }
    
    ${media.tablet} {
        width: 30px;
        height: 30px;
        font-size: 12px;
    }
    
    ${media.mobile} {
        width: 30px;
        height: 30px;
        font-size: 12px;
    }
`;

// Botón + propio de la vista Día.
// Ya no usamos el + del header general.
export const DayAddButton = styled.button`
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 8px;
    background: hsl(210, 40%, 96%);
    color: hsl(24, 85%, 60%);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    transition: background 0.2s ease, opacity 0.2s ease;

    &:hover {
        background: hsl(210, 40%, 92%);
        opacity: 0.9;
    }
`;

// Contenedor de filas de la vista Día.
// Scrollea internamente si hay muchos horarios.
export const DayEventsContainer = styled.div`
    padding: 0 28px 18px;
    flex: 1 1 auto;
    overflow-y: auto;
    overflow-x: hidden;
    height: 100%;
    min-height: 0;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: hsl(220, 13%, 95%);
        border-radius: 3px;
    }

    &::-webkit-scrollbar-thumb {
        background: hsl(220, 13%, 70%);
        border-radius: 3px;
        
        &:hover {
            background: hsl(220, 13%, 60%);
        }
    }
    
    ${media.tablet} {
        padding: 0 20px 16px;
    }
    
    ${media.mobile} {
        padding: 0 12px 12px;
    }
`;

export const DayEventsContainerRelative = styled(DayEventsContainer)`
    position: relative;
`;

// Contenedor general de la vista Día.
export const DayViewContainer = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

// Fila de cada horario/turno.
// Si está vacía, se ve más tenue.
// Si tiene turno, se ve como una fila de agenda.
export const DayTimeSlotRow = styled.div`
    display: grid;
    grid-template-columns: minmax(220px, 1fr) auto;
    align-items: center;
    gap: 16px;
    min-height: 48px;
    padding: 10px 8px 10px 12px;
    border-bottom: 1px solid hsl(220, 13%, 91%);
    border-left: ${props => props.$accentColor ? `5px solid ${props.$accentColor}` : '5px solid transparent'};
    transition: background 0.2s ease, opacity 0.2s ease;
    cursor: pointer;
    opacity: ${props => (props.isEmpty ? 0.72 : 1)};
    background: ${props => (props.isEmpty ? '#FFFFFF' : 'transparent')};

    &:hover {
        background: hsl(210, 40%, 96%);
        opacity: 1;
    }

    &:last-child {
        border-bottom: none;
    }
    
    ${media.tablet} {
        grid-template-columns: minmax(180px, 1fr) auto;
        gap: 12px;
        min-height: 46px;
        padding: 9px 6px;
    }
    
    ${media.mobile} {
        grid-template-columns: 1fr;
        align-items: stretch;
        gap: 8px;
        min-height: 48px;
        padding: 10px 4px;
    }
`;

// Bloque izquierdo: horario + paciente o texto disponible.
export const DayEventInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 18px;
    color: hsl(0, 0%, 25%);
    font-size: 13px;
    min-width: 0;
    
    ${media.tablet} {
        gap: 14px;
        font-size: 12px;
    }
    
    ${media.mobile} {
        gap: 10px;
        font-size: 12px;
        min-width: 0;
    }
`;

// Bloque derecho: recurrencia, video, honorarios y estado.
export const DayEventMeta = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 12px;
    
    ${media.tablet} {
        gap: 10px;
    }
    
    ${media.mobile} {
        justify-content: flex-start;
        gap: 8px;
        flex-wrap: wrap;
    }
`;

export const DayEventTimeSlot = styled.div`
    position: relative;
`;

// Horario del turno o de la fila vacía.
export const DayEventTime = styled.span`
    font-weight: 700;
    color: hsl(0, 0%, 20%);
    min-width: 105px;
    font-size: 13px;
    
    ${media.mobile} {
        min-width: 92px;
        font-size: 12px;
    }
`;

// Nombre del paciente.
export const DayEventName = styled.strong`
    color: hsl(0, 0%, 20%);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
`;

// Texto para filas sin turno.
export const DayEmptyText = styled.span`
    color: hsl(0, 0%, 50%);
    font-size: 12px;
    font-weight: 500;
`;

// Icono de recurrencia.
export const DayEventRecurrenceIcon = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 4px;
    flex-shrink: 0;
`;

// Botón de video.
export const DayEventVideoButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 6px 8px;
    border-radius: 8px;
    background-color: hsl(210, 40%, 96%);
    cursor: pointer;
    transition: background-color 0.2s ease;
    flex-shrink: 0;

    &:hover {
        background-color: hsl(210, 40%, 92%);
    }
`;

// Honorarios del turno.
export const DayEventPriceContainer = styled.div`
    font-size: 12px;
    font-weight: 600;
    color: hsl(0, 0%, 35%);
    flex-shrink: 0;
    padding: 6px 10px;
    background-color: hsl(210, 40%, 96%);
    border-radius: 8px;
    border: 1px solid hsl(220, 13%, 91%);
`;

// Contenedor del dropdown de estado.
export const DayEventStatusWrapper = styled.div`
    flex-shrink: 0;
    padding: 3px;
    background-color: hsl(210, 40%, 96%);
    border-radius: 8px;
    border: 1px solid hsl(220, 13%, 91%);
`;

// Panel de acciones de la vista Día.
// Tiene +, ruedita y warning, igual que la vista Semana.
export const DayActionsPanel = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    flex-shrink: 0;
`;

// Botones chicos de acción de la vista Día.
// warning cambia el color para el aviso de turnos ocultos.
export const DayIconButton = styled.button`
    width: 34px;
    height: 34px;
    border: none;
    border-radius: 8px;
    background: ${props => props.warning ? '#FEF3C7' : 'hsl(210, 40%, 96%)'};
    color: ${props => props.warning ? '#B45309' : 'hsl(24, 85%, 60%)'};
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    font-size: 16px;
    font-weight: 700;
    transition: background 0.2s ease, opacity 0.2s ease;

    &:hover {
        background: ${props => props.warning ? '#FDE68A' : 'hsl(210, 40%, 92%)'};
        opacity: 0.9;
    }
`;

// Fila de hora actual dentro de la vista Día.
// No representa un turno: es solo una referencia visual y punto de autoscroll.
export const DayCurrentTimeRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    height: 28px;
    margin: 4px 0;
    color: #ef4444;
    font-size: 11px;
    font-weight: 700;

    &::after {
        content: '';
        height: 2px;
        background: #ef4444;
        flex: 1;
        border-radius: 999px;
    }
`;

// Puntito rojo de la fila de hora actual.
export const DayCurrentTimeDot = styled.div`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #ef4444;
    flex-shrink: 0;
`;

// Etiqueta de hora actual.
export const DayCurrentTimeLabel = styled.div`
    background: #ef4444;
    color: #FFFFFF;
    padding: 2px 6px;
    border-radius: 4px;
    white-space: nowrap;
`;

// Ícono de notificación en la vista Día.
// Siempre muestra un sobre como base, y arriba un indicador de estado.
// Así el usuario entiende que el problema/estado pertenece al email.
export const DayEventNotificationIcon = styled.div`
    width: ${props => props.$variant === 'danger' ? '52px' : '38px'};
    height: 30px;
    border-radius: 9px;
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    cursor: default;
    border: 1px solid hsl(220, 13%, 88%);

    background: ${props => {
        if (props.$variant === 'danger') {
            return '#FEE2E2';
        }

        if (props.$variant === 'warning') {
            return '#FEF3C7';
        }

        if (props.$variant === 'success') {
            return '#DCFCE7';
        }

        if (props.$variant === 'info') {
            return '#DBEAFE';
        }

        if (props.$variant === 'scheduled') {
            return 'hsl(24, 95%, 96%)';
        }

        return 'hsl(210, 40%, 96%)';
    }};

    .notification-mail-symbol {
        font-size: 16px;
        line-height: 1;
        color: ${props => {
            if (props.$variant === 'danger') {
                return '#B91C1C';
            }

            if (props.$variant === 'warning') {
                return '#B45309';
            }

            if (props.$variant === 'success') {
                return '#15803D';
            }

            if (props.$variant === 'info') {
                return '#1D4ED8';
            }

            if (props.$variant === 'scheduled') {
                return 'hsl(24, 85%, 50%)';
            }

            return 'hsl(0, 0%, 45%)';
        }};
    }

    .notification-status-badge {
        min-width: ${props => props.$variant === 'danger' ? '18px' : '16px'};
        height: 16px;
        padding: ${props => props.$variant === 'danger' ? '0 4px' : '0 2px'};
        border-radius: 999px;
        position: absolute;
        right: -7px;
        top: -7px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: ${props => props.$variant === 'danger' ? '9px' : '10px'};
        line-height: 1;
        font-weight: 900;
        border: 1px solid #fff;
        box-shadow: 0 1px 3px rgba(15, 23, 42, 0.18);

        background: ${props => {
            if (props.$variant === 'danger') {
                return '#DC2626';
            }

            if (props.$variant === 'warning') {
                return '#F59E0B';
            }

            if (props.$variant === 'success') {
                return '#16A34A';
            }

            if (props.$variant === 'info') {
                return '#2563EB';
            }

            if (props.$variant === 'scheduled') {
                return 'hsl(24, 85%, 50%)';
            }

            return '#6B7280';
        }};

        color: #fff;
    }

    ${media.mobile} {
        width: ${props => props.$variant === 'danger' ? '48px' : '36px'};
        height: 28px;

        .notification-mail-symbol {
            font-size: 15px;
        }

        .notification-status-badge {
            right: -6px;
            top: -6px;
        }
    }
`;